import {EditOutlined} from '@ant-design/icons';
import {Button, Form, Input, InputNumber, Modal, Select, Switch, Tag, Typography} from 'antd';
import PropTypes from 'prop-types';
import React from 'react';

const {Text} = Typography;
const {Option} = Select;

const STATUS_CONFIG = {
  PRESENT: {label: 'Present', color: 'success'},
  ABSENT:  {label: 'Absent',  color: 'error'},
  HALF_DAY:{label: 'Half Day', color: 'processing'},
};

const EditAttendance = ({open, onClose, record, onSubmit, loading}) => {
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (record && open) {
      form.setFieldsValue({
        status:        record.status,
        isOvertime: record.isOvertime ?? false,
        reason:        record.reason ?? '',
      });
    }
  }, [record, open]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = values => {
    if (!record) {return;}
    onSubmit({
      id:            record.id,
      employeeId:    record.employeeId,
      employeeName:  record.employeeName,
      status:        values.status,
      isOvertime: values.isOvertime ?? false,
      reason:        values.reason ?? '',
    });
  };

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-2'}>
          <EditOutlined className={'text-primary-500'} />
          <span>Edit Attendance Record</span>
        </div>
      )}
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      {record && (
        <div className={'mt-4'}>
          {/* Employee name pill */}
          <div className={'flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-5'}>
            <div className={'w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center'}>
              <span className={'text-primary-600 font-semibold text-sm'}>
                {record.employeeName?.trim().split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??'}
              </span>
            </div>
            <div>
              <Text className={'font-semibold text-gray-800 block'}>{record.employeeName}</Text>
              <Text className={'text-xs text-gray-400'}>
                {record.date} &nbsp;·&nbsp; Last marked by {record.markedByRole}
              </Text>
            </div>
          </div>

          <Form
            form={form}
            layout={'vertical'}
            onFinish={handleFinish}
          >
            <Form.Item
              name={'status'}
              label={'Attendance Status'}
              rules={[{required: true, message: 'Please select a status'}]}
            >
              <Select placeholder={'Select status'} className={'w-full'}>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <Option key={key} value={key}>
                    <Tag color={cfg.color} className={'mr-1'}>{cfg.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label={'Overtime'} name={'isOvertime'} valuePropName={'checked'} initialValue={false}>
              <Switch size={'medium'} />
            </Form.Item>

            <Form.Item name={'reason'} label={'Reason / Note'}>
              <Input.TextArea
                rows={3}
                placeholder={'Optional reason or note…'}
              />
            </Form.Item>

            <div className={'flex justify-end gap-2 mt-6'}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type={'primary'} htmlType={'submit'} loading={loading}>
                Save Changes
              </Button>
            </div>
          </Form>
        </div>
      )}
    </Modal>
  );
};

EditAttendance.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,

  record: PropTypes.shape({
    id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    employeeId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    employeeName: PropTypes.string,
    status: PropTypes.string,
    isOvertime: PropTypes.bool,
    reason: PropTypes.string,
    date: PropTypes.string,
    markedByRole: PropTypes.string,
  }),
};

export default EditAttendance;
